import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosDelegationService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type { CreateDelegationPayload } from '@/types/roros/actors';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosDelegations(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.delegations.all, params] as const,
    queryFn: () => RorosDelegationService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDelegation(id: string) {
  return useQuery({
    queryKey: rorosKeys.delegations.detail(id),
    queryFn: () => RorosDelegationService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosDelegationCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDelegationPayload) => RorosDelegationService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.delegations.all });
    },
  });
}

export function useRorosDelegationRevoke() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosDelegationService.revoke(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.delegations.all });
      qc.invalidateQueries({ queryKey: rorosKeys.delegations.detail(id) });
    },
  });
}
